<script setup lang="ts">
const basePath = import.meta.env.BASE_URL
const localItems = ['原始视频、封面、代理预览和完成导出', '项目配置、平台文案、处理参数与本地发布状态', '任务状态、应用设置和用户主动生成的诊断包']
const networkRows = [
  ['授权激活与试用', '授权码或随机安装标识、客户端版本、系统平台和必要请求信息'],
  ['规则与应用更新', '当前版本、系统与架构、更新请求结果'],
  ['AI 文案改写', '仅在用户选择服务商并确认后发送当前平台标题、正文、标签和改写要求'],
  ['淘宝购买', '由系统默认浏览器打开，发多多不读取淘宝账号、Cookie或订单数据'],
]
</script>

<template>
  <div class="page-wrap legal-page">
    <header class="page-heading">
      <h1>用户协议、隐私政策与第三方许可</h1>
      <p>以下内容适用于通过 ruwin.cn 自有官网分发的发多多桌面客户端。</p>
      <p class="effective-date">版本：M2 / 0.4.0　生效日期：2026-09-04</p>
    </header>

    <nav class="legal-nav" aria-label="法律文档目录">
      <a href="#agreement">用户协议</a><a href="#privacy">隐私政策</a><a href="#licenses">第三方许可</a><a href="#entity">主体与备案</a>
    </nav>

    <section id="agreement" class="legal-section ml-card">
      <h2>一、用户协议</h2>
      <h3>1. 产品范围</h3>
      <p>发多多是本地多平台内容适配工具，用于媒体探测、画面适配、封面和文案整理、素材包导出与本地发布状态记录。软件不会代替用户登录平台、自动发布、抓取平台账号数据或承诺审核和流量结果。</p>
      <h3>2. 授权与试用</h3>
      <p>M2 提供一次限时试用。试用开始、到期时间和限制在客户端透明展示；正式授权通过人工核单后发码。试用升级、授权到期、禁用或撤销不会主动删除用户本地项目、原始素材和完成导出。</p>
      <h3>3. 用户内容与合法使用</h3>
      <p>用户应当确保对导入、编辑和发布的素材具有合法权利，并自行复核平台规则、版权、个人信息和广告合规。发多多提供的是工具和提示，不构成平台审核、法律或商业效果保证。</p>
      <h3>4. 更新与服务变化</h3>
      <p>应用更新和规则更新使用独立签名通道。非安全强制更新允许稍后处理；更新失败不得破坏当前可用版本、项目或规则。功能、平台规则和第三方接口变化时，以客户端展示的版本说明为准。</p>
      <h3>5. 禁止行为</h3>
      <p>不得绕过授权、批量滥用试用、篡改更新包、利用软件侵害他人权益或实施违反适用法律和平台规则的行为。</p>
    </section>

    <section id="privacy" class="legal-section ml-card">
      <h2>二、隐私政策</h2>
      <div class="privacy-grid">
        <article><h3>默认留在本机</h3><ul><li v-for="item in localItems" :key="item">{{ item }}</li></ul></article>
        <article><h3>仅在明确用途时联网</h3><dl><template v-for="([title, detail]) in networkRows" :key="title"><dt>{{ title }}</dt><dd>{{ detail }}</dd></template></dl></article>
      </div>
      <h3>1. 最小设备信息</h3>
      <p>试用和授权使用客户端随机生成的安装标识，不读取硬盘序列号、主板序列号等过度硬件指纹。服务端仅保存必要摘要，用于限制重复领取、设备额度、撤销和审计。</p>
      <h3>2. AI 服务</h3>
      <p>AI 功能默认关闭。调用前会显示接收方和发送内容；不会发送原始视频、图片、封面、本地路径、其他平台文案、账号、Cookie或授权信息。API Key 写入操作系统安全凭据存储，不进入项目、日志、诊断包或浏览器存储。候选结果必须经用户确认后才覆盖当前平台文案。</p>
      <h3>3. 诊断与分享</h3>
      <p>诊断包由用户主动生成，默认不上传；生成后可查看包含项、排除项、大小和 SHA-256。诊断包自动排除原始素材、完整文案、项目名称、秘密和不必要设备标识，用户可以主动打开、分享或删除。</p>
      <h3>4. 数据删除</h3>
      <p>用户可以删除最近项目记录、受管缓存、诊断包和AI服务密钥。清理操作不会越权删除原始素材和完成导出；卸载时是否保留项目和设置由安装器提示及系统行为决定。</p>
    </section>

    <section id="licenses" class="legal-section ml-card">
      <h2>三、第三方软件与许可证</h2>
      <p>发多多使用 Tauri、Vue、Rust 生态组件，并内置固定来源的 FFmpeg、ffprobe 和 x264。内置媒体工具按 GPL 兼容分发链处理，完整 GPLv3 文本与来源说明随安装包和下载目录提供。</p>
      <p>发布时根据锁文件生成完整软件物料清单，记录组件名称、版本、许可证和来源。依赖版本或分发方式变化后必须重新生成并复核。</p>
      <a class="ml-button ml-button--secondary" :href="`${basePath}THIRD-PARTY-NOTICES.md`" target="_blank" rel="noreferrer">查看完整第三方清单</a>
    </section>

    <section id="entity" class="legal-section entity-section">
      <h2>四、经营主体与备案</h2>
      <p>经营主体应与官网备案主体、淘宝商品页、授权服务、用户协议、隐私政策和安装包发布者保持一致。当前页面公开展示 ICP 备案号：京ICP备2026056110号。</p>
      <p>完整个体工商户名称、公安联网备案号与官方查询链接属于正式公开销售前的外部核验项；在信息未由运营方提供并核对前，本页面不编造名称或备案号，也不把当前候选版本表述为已完成全部商业合规。</p>
    </section>
  </div>
</template>

<style scoped>
.effective-date { color: var(--ml-text-secondary); font-size: 13px; }
.legal-nav { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 28px; }
.legal-nav a { padding: 10px 16px; border-radius: 999px; background: var(--ml-brand-soft); color: var(--ml-brand); font-weight: 700; }
.legal-section { margin-top: 24px; padding: 32px; scroll-margin-top: 90px; }
.legal-section h2 { margin-top: 0; font-size: 24px; }
.legal-section h3 { margin: 26px 0 10px; font-size: 16px; }
.legal-section p, .legal-section li, .legal-section dd { color: var(--ml-text-secondary); line-height: 1.85; }
.privacy-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 22px; }
.privacy-grid article { padding: 22px; border-radius: 12px; background: #f8fafc; }
.privacy-grid h3 { margin-top: 0; }
.privacy-grid ul { display: grid; gap: 12px; padding-left: 20px; }
.privacy-grid dl { margin: 0; }
.privacy-grid dt { margin-top: 12px; font-weight: 700; }
.privacy-grid dd { margin: 4px 0 0; }
.entity-section { border-radius: 14px; background: var(--ml-warning-soft); }
.legal-section .ml-button { display: inline-flex; width: auto; margin-top: 12px; }
@media (max-width: 820px) { .privacy-grid { grid-template-columns: 1fr; } .legal-section { padding: 22px; } }
</style>
